/**
 * Privacy Policy
 *
 * To update the policy text, replace PRIVACY_POLICY_TEXT below with your
 * actual Privacy Policy content.
 *
 * Conversion tips for a .docx file:
 *   - Word:   Open → Ctrl+A → Ctrl+C → paste below (clean up smart-quotes if needed)
 *   - Pandoc: pandoc your-privacy-policy.docx -t plain -o privacy.txt  →  copy here
 *   - Node:   npx mammoth your-privacy-policy.docx --output-format=markdown  →  copy here
 *
 * After updating the text, increment PRIVACY_POLICY_VERSION so it can be
 * tracked alongside the Terms of Service version.
 */

export const PRIVACY_POLICY_VERSION = '1.0';

export const PRIVACY_POLICY_TEXT = `Child Nav, Inc.
Privacy Policy
Effective Date: 5/1/2026  •  Version 1.0
Applies to: Child Nav Family App (iOS & Android) and www.childnav.com
OUR COMMITMENT:  Child Nav exists to support parents and children — not to monetize your data. We will never sell your personal information or your child’s health data to advertisers, data brokers, or commercial third parties.

1.  Who We Are
Child Nav, Inc. (“Child Nav,” “we,” “our,” or “us”) is a Colorado corporation that operates the Child Nav Family App (the “App”) and the website located at www.childnav.com (the “Site”). Child Nav provides parents and caregivers with continuous, evidence-based developmental monitoring tools for children from birth through age five.

This Privacy Policy describes how we collect, use, protect, and share information about you and your child when you use the App or visit the Site. It applies equally to both the App and the Site unless a section specifically notes otherwise.

Child Nav is the data controller for personal information collected through both the App and the Site. For questions or requests related to this Policy, contact us at:

Child Nav, Inc. — Privacy Office
Email: privacy@childnav.com
Website: www.childnav.com/privacy

2.  Scope of This Policy
This Policy applies to:
•	All users of the Child Nav Family App on iOS and Android;
•	All visitors to www.childnav.com, including any web-based versions of the App; and
•	Any communications between you and Child Nav (email, support, feedback forms).

This Policy does not apply to Child Nav’s separate provider-facing products (Patient Tools) or to third-party websites or services that may be linked from the App or Site. Those services have their own privacy policies.

3.  Information We Collect
We collect information in three ways: information you give us directly, information generated automatically when you use the App or Site, and information we derive from what you provide.

3.1  Information You Provide Directly
Account Registration
•	Full name and email address;
•	Your relationship to the child (parent, legal guardian, authorized caregiver); and
•	Notification preferences.

Child Profile Information
•	Child’s first name or nickname (optional);
•	Child’s date of birth or age range; and
•	Sex assigned at birth (used for age-normed developmental calculations).

Developmental & Health Information (Protected Health Information / PHI)
The following categories constitute Protected Health Information (PHI) under HIPAA when linked to an identifiable individual:
•	Milestone observations and assessment responses you enter for your child across five developmental domains: cognitive, communication, fine motor, gross motor, and social-emotional;
•	Developmental screening scores, domain summaries, and developmental age estimates generated by our Item Response Theory (IRT) engine;
•	Parental concerns and free-text notes you submit within the App;
•	Well-visit preparation summaries and provider communication artifacts you generate; and
•	Any additional health, behavioral, or developmental information you voluntarily enter.

NOTE:  Parental concerns are treated as first-class clinical inputs in our Integrated Pediatric Screening (IPS) probability network — not as informal annotations. They are protected with the same rigor as all other PHI.

Communications
•	Messages you send to our support team;
•	Survey or feedback responses you submit; and
•	Email correspondence with Child Nav.

3.2  Information Collected Automatically
App Usage Data
•	Session start and end timestamps;
•	Features accessed and interaction patterns (e.g., modules opened, assessments completed);
•	In-app navigation paths; and
•	Crash reports and error logs.

Device & Technical Data
•	Device type, manufacturer, and model;
•	Operating system and version;
•	App version;
•	A unique device identifier or installation ID (not linked to your name); and
•	IP address (truncated; used for general geographic region only, not precise location).

Website Data (Site Visitors)
•	Browser type and version;
•	Pages visited and time spent on each page;
•	Referring URL; and
•	Cookie data as described in Section 9.

3.3  Derived Information
We derive the following from information you provide:
•	Developmental age estimates across five domains, calculated using our IRT-based scoring methodology;
•	Probability scores indicating likelihood of developmental concerns in specific areas, generated by the IPS network; and
•	Well-visit readiness summaries synthesizing milestone data, concerns, and domain scores.

Derived outputs are used to personalize your experience and are subject to the same protections as the underlying PHI.

4.  How We Use Your Information

Data Category	What We Do With It
Account Information	Operate your account; send transactional communications.
Child Health / PHI	Generate developmental scores; prepare well-visit summaries; improve the App.
Parental Concerns	First-class input into developmental probability calculations; never shared without consent.
Usage & Technical Data	App performance, crash diagnostics, feature improvement.
De-Identified Data	Population norms research; IRT model calibration. Never sold commercially.

In more detail, we use your information to:

4.1  Provide and Operate the App and Site
•	Create and manage your account;
•	Calculate developmental age estimates and generate IPS probability scores for your child;
•	Display milestone tracking progress and growth over time;
•	Generate well-visit preparation summaries for healthcare appointments; and
•	Enable provider sharing features when you explicitly authorize them.

4.2  Improve Our Services
•	Diagnose technical problems and fix bugs;
•	Analyze aggregate usage patterns to improve App features and content; and
•	Calibrate and improve our IRT scoring models and IPS network using de-identified, aggregated data (see Section 6).

4.3  Communicate With You
•	Send transactional messages (account confirmations, security alerts, password resets);
•	Deliver developmental milestone reminders and well-visit preparation prompts;
•	Provide customer support; and
•	Send product updates and educational content (you may opt out at any time).

4.4  Legal and Safety Purposes
•	Comply with applicable law, regulation, or legal process;
•	Enforce our Terms of Service; and
•	Protect the rights, property, or safety of Child Nav, our users, or the public.

WE DO NOT:  Use your information or your child’s health data to serve targeted advertisements. Child Nav products are ad-free. We do not allow advertisers to access or influence your experience.

5.  Legal Basis for Processing
Depending on your jurisdiction, our legal basis for processing your personal information includes:
•	Performance of a contract — processing necessary to provide the App and services you requested;
•	Consent — for optional features such as research data contribution, push notifications, and provider sharing, where we ask for your affirmative consent;
•	Legitimate interests — for App improvement, security, fraud prevention, and aggregate analytics, where our interests do not override your rights; and
•	Legal obligation — for compliance with HIPAA, applicable state health data laws, and other legal requirements.

Where we rely on consent, you may withdraw it at any time through the App’s Settings or by contacting us at privacy@childnav.com, without affecting the lawfulness of processing prior to withdrawal.

6.  De-Identified Data and Research
OUR COMMITMENT:  De-identified data will never be sold to commercial third parties, advertisers, or data brokers. Its use is strictly limited to improving developmental science and the accuracy of the App.

6.1  De-Identification Standard
With your consent, Child Nav may de-identify your data using the HIPAA Expert Determination or Safe Harbor method (45 C.F.R. § 164.514). Once de-identified, the data carries no reasonable basis for identifying you or your child and is no longer considered PHI under HIPAA.

6.2  Permitted Uses of De-Identified Data
De-identified data may be used for:
•	Calibration and ongoing refinement of our population-level developmental normative database;
•	Training and validation of our IRT-based scoring models and IPS network;
•	Peer-reviewed academic and clinical research to advance the science of pediatric developmental screening;
•	Quality assurance and internal algorithm improvement; and
•	Publication of aggregate, anonymized findings (no individual can be identified in any publication).

6.3  Your Control
Participation in research data contribution is opt-in data connections and can be changed at any time under Connections > Data Connections. Opting out has no effect on your access to any App features.

De-identified data already incorporated into population-level aggregate datasets prior to your opt-out cannot be removed from those aggregates, as it is no longer linked to you in any way.

7.  How We Share Your Information
Child Nav does not sell personal information. We share information only in the limited circumstances described below.

7.1  With Your Explicit Authorization
•	Healthcare providers: If you use the well-visit sharing feature, your child’s developmental summary is shared only with the specific provider(s) you authorize at the time of sharing. You may revoke access through the App.
•	Co-parent or co-guardian: You may choose to share your account access with another caregiver. You control who has access.

7.2  With Service Providers
We engage carefully vetted third-party vendors to help operate the App and Site. These vendors process data only on our instructions and are prohibited from using it for their own purposes. Where required by HIPAA, we execute Business Associate Agreements (BAAs) with these vendors. Categories include:
•	Cloud infrastructure and hosting providers;
•	Authentication and security services;
•	Customer support platforms; and
•	Analytics providers (receiving only de-identified or aggregate data).

7.3  For Legal Compliance
We may disclose information if required by law, court order, subpoena, or government authority, or where we believe in good faith that disclosure is necessary to protect the rights, property, or safety of Child Nav, our users, or the public.

7.4  In a Business Transaction
If Child Nav is involved in a merger, acquisition, asset sale, or similar transaction, your information may be transferred to the successor entity. We will notify you via email and/or prominent notice in the App before any such transfer, and the successor entity will be bound to honor the commitments in this Policy or obtain your fresh consent.

7.5  Aggregate or De-Identified Information
We may share aggregate, anonymized statistics about App usage or population developmental trends publicly or with research partners. This information cannot be used to identify you or your child.

WE WILL NEVER:  Share your PHI with employers, insurers, schools, government agencies, or any other party for purposes beyond those described in this Policy, without your explicit written consent.

8.  Data Security
Child Nav takes the security of your information seriously. We implement the following administrative, technical, and physical safeguards:

8.1  Technical Safeguards
•	AES-256 encryption of all PHI stored at rest;
•	TLS 1.2 or higher encryption for all data transmitted between your device and our servers;
•	Secure, isolated storage of encryption keys separate from encrypted data;
•	Multi-factor authentication options for account access;
•	Automated vulnerability scanning and regular security assessments; and
•	Intrusion detection monitoring on all production systems.

8.2  Administrative Safeguards
•	Role-based access controls limiting PHI access to authorized personnel with a documented business need;
•	Comprehensive workforce security training and HIPAA awareness programs;
•	Documented security incident response procedures;
•	Business Associate Agreements with all vendors that handle PHI; and
•	Regular risk assessments in accordance with HIPAA Security Rule requirements.

8.3  Physical Safeguards
•	All production systems hosted in SOC 2 Type II certified data centers with physical access controls; and
•	No PHI stored on unencrypted portable media.

Despite these measures, no system is completely secure. In the event of a breach affecting your PHI, we will notify you and applicable regulatory authorities as required by HIPAA’s Breach Notification Rule (45 C.F.R. §§ 164.400–414) and applicable state breach notification laws.

9.  Cookies and Tracking Technologies
9.1  App
The Child Nav Family App does not use advertising cookies or third-party tracking pixels. We use minimal, session-based local storage for authentication state and user preferences only.

9.2  Website (www.childnav.com)
Our website uses the following categories of cookies:
•	Strictly necessary cookies: Required for the Site to function (authentication, security, session management). Cannot be disabled.
•	Functional cookies: Remember your preferences and settings (e.g., language). You may disable these in your browser settings.
•	Analytics cookies: Collect anonymized information about how visitors use the Site (e.g., pages visited, time on page). We use this data in aggregate only to improve the Site. You may opt out through our cookie preference center.

We do not use advertising cookies or sell data collected through cookies to third parties. You can manage cookie preferences at any time through the cookie settings link in the footer of our website.

10.  Data Retention
We retain your information for as long as your account is active or as necessary to provide the App, comply with legal obligations, resolve disputes, and enforce our agreements. Specific retention periods:

•	Account and PHI: Retained while your account is active. Upon account deletion, personal information is permanently deleted, subject to any legal hold or minimum retention requirement.
•	De-identified aggregate data: Retained indefinitely as part of population normative datasets. Cannot be linked back to you once de-identified.
•	Technical logs: Retained for up to 12 months for security and diagnostic purposes, then automatically deleted.
•	Provider-shared reports: Reports transmitted to healthcare providers may be retained by those providers under their own records policies. Child Nav’s copy is deleted with your account.
•	Legal hold: If your information is subject to a legal hold or government request, retention may be extended until the hold is released.

11.  Children’s Privacy (COPPA)
The App and Site are designed for use by parents, guardians, and authorized adult caregivers. We do not knowingly permit children under 13 to create accounts or directly submit personal information.

Developmental data entered about a child is submitted by the adult account holder, not the child. The adult account holder is responsible for all information entered and for ensuring the child’s data is entered only for legitimate caregiving purposes.

If you believe a child under 13 has directly created an account or submitted personal information without parental consent, please contact us immediately at privacy@childnav.com. We will promptly investigate and delete any such information.

12.  Your Privacy Rights
Depending on your jurisdiction, you may have the following rights with respect to your personal information. To exercise any of these rights, contact us at privacy@childnav.com or through the App’s Settings > Privacy.

12.1  Rights Available to All Users
•	Access: Access the personal information we hold about you.
•	Deletion: Access to delete your account and associated personal information.
•	Opt-out of research: Withdraw consent to use your de-identified data for research purposes.
•	Opt-out of marketing: Unsubscribe from non-transactional communications at any time.

12.2  California Residents (CCPA / CPRA)
California residents have additional rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):
•	Right to Know: Request disclosure of the categories and specific pieces of personal information we have collected, used, disclosed, or sold.
•	Right to Delete: Request deletion of personal information, subject to certain exceptions.
•	Right to Correct: Request correction of inaccurate personal information.
•	Right to Opt-Out of Sale or Sharing: We do not sell or share personal information as defined under California law. No opt-out is necessary, but you may submit a “Do Not Sell or Share” request at any time.
•	Right to Limit Use of Sensitive Personal Information: You may direct us to limit use of sensitive personal information (including health data) to purposes necessary to provide the App.
•	Right to Non-Discrimination: We will not discriminate against you for exercising any CCPA/CPRA right.

To submit a California privacy request, contact privacy@childnav.com. We will respond within 45 days, with a 45-day extension available if necessary.

12.3  Colorado, Virginia, Connecticut, and Other State Privacy Laws
Residents of states with comprehensive consumer privacy laws (including Colorado CPA, Virginia CDPA, Connecticut CTDPA, and others) have rights that may include access, correction, deletion, portability, and the right to opt out of certain processing. Contact privacy@childnav.com to submit a request. We will respond within 45 days.

12.4  State Health Data Privacy
Several states (including Washington, Nevada, and others) have enacted health data-specific privacy laws that provide additional protections beyond HIPAA for consumer health data collected outside of traditional healthcare relationships. Where applicable, Child Nav complies with these state-specific requirements. Contact privacy@childnav.com for jurisdiction-specific inquiries.

12.5  HIPAA Rights
To the extent your information constitutes PHI under HIPAA and Child Nav acts as a covered entity or business associate with respect to that information, you may have additional rights including the right to access, amend, and request an accounting of disclosures of your PHI. Contact privacy@childnav.com to submit a HIPAA access or amendment request.

12.6  Response Timeframes
We will acknowledge all privacy rights requests within 5 business days and provide a substantive response within 30 days (or 45 days where permitted by applicable law). We will not charge a fee for reasonable requests. We may require identity verification before fulfilling a request.

13.  International Users
Child Nav is operated from the United States. If you access the App or Site from outside the United States, your information will be transferred to and processed in the United States, where privacy laws may differ from those in your country.

For users in the European Economic Area (EEA), United Kingdom, or Switzerland: At this time, Child Nav’s primary market is the United States. If you access the App from these jurisdictions, please be aware that your data will be processed in the U.S. We are evaluating GDPR compliance obligations and will update this Policy as appropriate. Contact privacy@childnav.com with GDPR-related inquiries.

14.  Third-Party Links and Services
The App and Site may contain links to third-party websites, resources, or services (such as the AAP HealthyChildren.org or CDC Learn the Signs. Act Early. resources referenced in educational content). Child Nav does not control and is not responsible for the privacy practices of these third parties.

We encourage you to review the privacy policies of any third-party services you visit. The inclusion of a link does not imply endorsement of that site’s privacy practices.

15.  Changes to This Privacy Policy
We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes, we will:
•	Post the updated Policy on this page with a new “Effective Date;”
•	Display a prominent notice within the App for at least 14 days following the update; and
•	Send an email notification to your registered address for significant changes affecting how we use PHI.

Your continued use of the App or Site after the effective date of any updated Policy constitutes your acceptance of the changes. If you do not agree to the updated Policy, please discontinue use and delete your account.

Prior versions of this Policy are available upon request at privacy@childnav.com.

16.  How to Contact Us
For any questions, concerns, or requests related to this Privacy Policy or your personal information, please contact us:

Child Nav, Inc. — Privacy Office
Email: privacy@childnav.com
Legal: legal@childnav.com
Website: www.childnav.com/privacy
Mailing Address: 6638 W. Ottawa Ave, Littleton, Colorado 80128
We aim to respond to all inquiries within 5 business days.

— End of Privacy Policy —
Document Version: 1.0  •  Effective Date: 5/1/2026
`.trim();
